import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DocumentsService, IDownloadableAsset } from '@lassi/webapp-shared';
import { LoadingButtonDirective } from '../loading-button.directive';

@Component({
  selector: 'lib-asset-list-item',
  imports: [
    MatIcon,
    MatButtonModule,
    MatProgressSpinnerModule,
    LoadingButtonDirective,
  ],
  template: `
    <button
      mat-stroked-button
      (click)="handleClick($event)"
      [loading]="loading()"
    >
      <div class="asset-contents">
        <mat-icon>image</mat-icon>
        <span>
          {{ asset().name }}
        </span>
        <mat-icon>download</mat-icon>
      </div>
    </button>
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;

    :host {
      display: block;
    }

    a,
    button {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: stretch;

      @include mat.button-overrides(
        (
          outlined-label-text-color:
            mat.get-theme-color(theme.$light-theme, on-surface),
        )
      );

      &:not(:first-of-type) {
        margin-top: 0.5rem;
      }

      .asset-contents {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        gap: 0.5rem;

        span {
          flex-grow: 1;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
          text-align: left;
        }

        :not(span) {
          flex-shrink: 0;
        }
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetListItemComponent {
  readonly documentsService = inject(DocumentsService);
  private readonly destroyRef = inject(DestroyRef);

  asset = input.required<IDownloadableAsset>();

  download = output<boolean>();

  loading = signal(false);

  handleClick(event: Event) {
    event.preventDefault();
    this.loading.set(true);
    this.documentsService
      .handleClick(this.asset().link)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.download.emit(result);
        this.loading.set(false);
      });
  }
}
